import type { Category, Todo } from "../../domain/entities.ts";
import type { TaskSubmissionDependencies } from "./task-submission-controller.ts";
import type { CreateTaskSaveMode } from "./task-panel-view.ts";
import { applyTaskForm } from "./task-form-mapper.ts";
import { wouldCreateDependencyCycle } from "./productivity.ts";

export interface TaskSubmissionRuntimeCapabilities {
  tasks(): Todo[];
  category(id: string | null): Category | undefined;
  clearRecoveryTimer(): void;
  validate(form: HTMLFormElement): boolean;
  reveal(group: string): void;
  toast(message: string, error?: boolean): void;
  saveMode(): CreateTaskSaveMode;
  loadPrevious(id: string): Promise<Todo | undefined>;
  create(id: string, update: (todo: Todo) => void): Promise<Todo>;
  applyBoardDefaults(todo: Todo, category: Category): void;
  update(id: string, update: (todo: Todo) => void): Promise<Todo | undefined>;
  nextOccurrence(todo: Todo): Promise<unknown>;
  setPanel(value: { draft: Todo | null; selectedId: string | null; editing: boolean }): void;
  setSaveMode(mode: CreateTaskSaveMode): void;
  clearCreateRecovery(): Promise<void>;
  clearEditRecovery(id: string): Promise<void>;
  persistSaveMode(mode: CreateTaskSaveMode): Promise<unknown>;
  setDraftRecovered(value: boolean): void;
  emptyDraft(): Todo;
  render(): Promise<unknown>;
  moved(previous: Todo): void;
  presence(id: string): void;
  sync(): void;
  markLocal(): void;
  randomId(): string;
}

export function createTaskSubmissionRuntime(capabilities: TaskSubmissionRuntimeCapabilities): TaskSubmissionDependencies {
  return {
    clearRecoveryTimer: capabilities.clearRecoveryTimer, validate: capabilities.validate, reveal: capabilities.reveal, toast: capabilities.toast,
    createsCycle: (id, dependencies) => wouldCreateDependencyCycle(id, dependencies, capabilities.tasks()),
    rememberedSaveMode: capabilities.saveMode, previous: capabilities.loadPrevious,
    applyForm: (todo, form, data, previous, externalCalendars) => applyTaskForm(todo, form, data, { previous, externalCalendars, randomId: capabilities.randomId, category: capabilities.category }),
    category: capabilities.category,
    create: async (id, update) => capabilities.create(id, todo => {
      update(todo);
      const category = capabilities.category(todo.categoryId);
      if (category?.boardColumns?.length) capabilities.applyBoardDefaults(todo, category);
    }),
    update: capabilities.update, nextOccurrence: capabilities.nextOccurrence, setPanel: capabilities.setPanel,
    persistRecovery: async (creating, id, mode) => {
      if (!creating) { await capabilities.clearEditRecovery(id); return; }
      capabilities.setSaveMode(mode);
      await Promise.all([capabilities.clearCreateRecovery(), capabilities.persistSaveMode(mode)]);
      capabilities.setDraftRecovered(false);
    },
    emptyDraft: capabilities.emptyDraft, render: capabilities.render, moved: capabilities.moved, presence: capabilities.presence,
    sync: capabilities.sync, markLocal: capabilities.markLocal,
  };
}
